import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle, XCircle, AlertCircle, Download, PlayCircle, FileText, Loader, ChevronDown, ChevronUp, RefreshCw, AlertTriangle, MessageSquare } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api';

interface ValidationReportPanelProps {
  processMapId: number;
  projectId: number;
}

interface ValidationStatus {
  has_validation: boolean;
  validation_id?: number;
  status?: string;
  total_steps?: number;
  completed_steps?: number;
  match_percentage?: number;
  confidence_score?: number;
  overall_assessment?: string;
  report_generated?: boolean;
  started_at?: string;
  completed_at?: string;
  error_message?: string;
  estimated_progress?: number;
  elapsed_minutes?: number;
  is_stuck?: boolean;
  message?: string;
  // Batch processing fields
  current_batch?: number;
  total_batches?: number;
  batch_size?: number;
  parallelism?: number;
  progress_percentage?: number;
  has_new_results?: boolean;
  validation_results?: ValidationResult[];
  gap_detection_results?: GapDetectionResult[];
  summary?: {
    clear_matches: number;
    ambiguities: number;
    inferences: number;
    missing: number;
    needs_review: number;
    errors?: number;
    successful_validations?: number;
    evidence_quality_distribution?: {
      strong: number;
      moderate: number;
      weak: number;
    };
    gap_detection?: {
      total_missing_steps: number;
      critical_gaps: number;
      high_gaps: number;
      medium_gaps: number;
      low_gaps: number;
    };
  };
}

interface ValidationResult {
  process_step_name: string;
  validation_status: string;
  confidence_score: number;
  evidence_quality?: string;
  source_reference?: {
    document_name: string;
    section: string;
    page: string;
    quoted_text: string;
  };
  reasoning?: string;
  assumptions_made?: string[];
  ambiguities_found?: string[];
  missing_information?: string[];
  systems_involved_verification?: {
    explicitly_mentioned: boolean;
    systems_found_in_source: string[];
    systems_not_found_in_source: string[];
    source_reference: string;
    confidence_score: number;
    notes: string;
  };
  stakeholder_verification?: {
    explicitly_named: boolean;
    stakeholder_found_in_source: string | null;
    source_reference: string;
    confidence_score: number;
    notes: string;
  };
  suggested_action?: string;
  error_message?: string;
}

interface GapDetectionResult {
  gap_id: string;
  step_name: string;
  description: string;
  source_reference: {
    document_name: string;
    section: string;
    page: string;
    quoted_text: string;
  };
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  reasoning: string;
  suggested_placement: string;
  confidence_score: number;
  detected_at: string;
}

interface DetailedReport {
  validation_results: ValidationResult[];
  gap_detection_results?: GapDetectionResult[];
}

const ValidationReportPanel: React.FC<ValidationReportPanelProps> = ({ processMapId, projectId }) => {
  const [validationStatus, setValidationStatus] = useState<ValidationStatus | null>(null);
  const [detailedReport, setDetailedReport] = useState<DetailedReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [showDetailedResults, setShowDetailedResults] = useState(false);
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set());
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [showGapDetection, setShowGapDetection] = useState(false);
  const [expandedGaps, setExpandedGaps] = useState<Set<number>>(new Set());
  const [lastBatchSeen, setLastBatchSeen] = useState(0);
  const [sendingGaps, setSendingGaps] = useState(false); // Track last batch for incremental loading

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollAttemptsRef = useRef(0);
  const maxPollAttempts = 240; // 20 minutes max (5 second intervals) - Increased for batch processing
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    loadValidationStatus();

    return () => {
      mountedRef.current = false;
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [processMapId]);

  const loadValidationStatus = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('jarvis_access_token');
      const response = await api.get(`/api/validation/process-map/${processMapId}/validation-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (mountedRef.current) {
        setValidationStatus(response.data);

        // If validation is stuck, show warning
        if (response.data.is_stuck) {
          toast.error(
            'Previous validation timed out. Please start a new validation.',
            { duration: 6000 }
          );
        }

        // If validation is in progress, start polling
        if (response.data.status === 'IN_PROGRESS') {
          startPolling();
        }
      }
    } catch (error: any) {
      console.error('Failed to load validation status:', error);
      if (mountedRef.current && error.response?.status !== 404) {
        toast.error('Failed to load validation status');
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  };

  const startPolling = () => {
    // Clear any existing polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    console.log('🔄 [POLLING] Starting polling interval (every 5 seconds)...');
    pollAttemptsRef.current = 0;
    setLastBatchSeen(0); // Reset batch tracking

    pollingIntervalRef.current = setInterval(async () => {
      console.log('📡 [POLLING] Fetching partial results...');
      if (!mountedRef.current) {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
        return;
      }

      pollAttemptsRef.current++;

      // Check for timeout
      if (pollAttemptsRef.current > maxPollAttempts) {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
        setValidating(false);
        toast.error(
          'Validation is taking longer than expected. It may still be processing. Please refresh to check status.',
          { duration: 8000 }
        );
        return;
      }

      try {
        const token = localStorage.getItem('jarvis_access_token');

        // Use partial-results endpoint for incremental loading
        const statusResponse = await api.get(
          `/api/validation/process-map/${processMapId}/partial-results?lastBatch=${lastBatchSeen}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const status = statusResponse.data;

        console.log('📥 [POLLING] Received response:', {
          status: status.status,
          current_batch: status.current_batch,
          total_batches: status.total_batches,
          completed_steps: status.completed_steps,
          total_steps: status.total_steps,
          progress_percentage: status.progress_percentage,
          has_new_results: status.has_new_results,
          validation_results_count: status.validation_results?.length || 0
        });

        if (mountedRef.current) {
          console.log('📝 [POLLING] Updating validationStatus state...');
          setValidationStatus(status);

          // Update incremental results as batches complete
          if (status.has_new_results && status.validation_results && status.validation_results.length > 0) {
            console.log(`📊 [FRONTEND] New results received: Batch ${status.current_batch}/${status.total_batches}, ${status.completed_steps}/${status.total_steps} steps`);

            // Update detailed report with incremental results
            setDetailedReport({
              validation_results: status.validation_results,
              gap_detection_results: status.gap_detection_results || []
            });

            // Auto-show detailed results when we have data
            if (!showDetailedResults && status.validation_results.length > 0) {
              setShowDetailedResults(true);
            }

            // Update last batch seen
            setLastBatchSeen(status.current_batch || 0);
          }

          if (status.status === 'COMPLETED') {
            console.log('✅ [POLLING] Validation COMPLETED!');
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
            }
            setValidating(false);

            const totalTime = status.completed_at && status.started_at
              ? ((new Date(status.completed_at).getTime() - new Date(status.started_at).getTime()) / 1000).toFixed(0)
              : 'unknown';

            console.log(`🎉 [POLLING] Total time: ${totalTime}s, Steps: ${status.completed_steps || status.total_steps}`);
            toast.success(
              `Validation completed! ${status.completed_steps || status.total_steps} steps validated in ${totalTime}s`,
              { icon: '✅', duration: 5000 }
            );

            // Ensure final results are loaded
            if (status.validation_results && status.validation_results.length > 0) {
              setDetailedReport({
                validation_results: status.validation_results,
                gap_detection_results: status.gap_detection_results || []
              });
              setShowDetailedResults(true);
            } else if (status.validation_id) {
              // Fallback: Load detailed report if partial results didn't work
              loadDetailedReport(status.validation_id);
            }
          } else if (status.status === 'FAILED') {
            console.error('❌ [POLLING] Validation FAILED:', status.error_message);
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
            }
            setValidating(false);
            toast.error(`Validation failed: ${status.error_message || 'Unknown error'}`, {
              duration: 6000
            });
          } else if (status.status === 'IN_PROGRESS') {
            console.log('⏳ [POLLING] Still IN_PROGRESS, waiting for next poll...');
          }
        }
      } catch (error: any) {
        console.error('Polling error:', error);

        // Don't stop polling on transient network errors
        if (error.response?.status === 404 || error.response?.status === 403) {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
          }
          setValidating(false);
          toast.error('Validation not found or access denied');
        }
      }
    }, 5000); // Poll every 5 seconds
  };

  const startValidation = async () => {
    if (!window.confirm('This will validate all process steps against source documentation using AI. This may take several minutes depending on the number of steps. Continue?')) {
      return;
    }

    console.log('🚀 [START] Starting validation for process map:', processMapId);

    try {
      setValidating(true);
      pollAttemptsRef.current = 0;
      const token = localStorage.getItem('jarvis_access_token');

      const startToastId = toast.loading('Starting validation process...', { duration: Infinity });

      console.log('📡 [START] Calling validate API endpoint...');
      const response = await api.post(
        `/api/validation/process-map/${processMapId}/validate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('✅ [START] Validate API response:', response.data);
      toast.dismiss(startToastId);

      if (response.data.status === 'IN_PROGRESS') {
        const estimatedMinutes = response.data.estimated_duration_minutes || 5;
        console.log('🔄 [START] Validation IN_PROGRESS, starting polling...');
        toast.success(
          `Validation started! Processing ${response.data.process_steps_count} steps. Estimated time: ~${estimatedMinutes} minutes.`,
          { duration: 5000, icon: '🔄' }
        );

        // Start polling for status
        startPolling();

        // Update status immediately
        await loadValidationStatus();
      } else {
        console.warn('⚠️ [START] Unexpected status:', response.data.status);
      }
    } catch (error: any) {
      console.error('Validation error:', error);
      setValidating(false);

      if (error.response?.status === 409) {
        toast.error('Validation already in progress. Please wait for it to complete.', {
          duration: 5000
        });
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.details || 'Cannot validate process map', {
          duration: 5000
        });
      } else {
        toast.error(error.response?.data?.error || 'Failed to start validation', {
          duration: 5000
        });
      }
    }
  };

  const loadDetailedReport = async (validationId: number) => {
    try {
      const token = localStorage.getItem('jarvis_access_token');
      const response = await api.get(
        `/api/validation/validation/${validationId}/report`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (mountedRef.current) {
        setDetailedReport(response.data);
      }
    } catch (error: any) {
      console.error('Failed to load detailed report:', error);
      if (error.response?.status !== 400) {
        toast.error('Failed to load detailed validation results');
      }
    }
  };

  const generatePDFReport = async () => {
    if (!validationStatus?.validation_id) return;

    try {
      setGeneratingPDF(true);
      const token = localStorage.getItem('jarvis_access_token');

      toast.loading('Generating PDF report...', { id: 'pdf-generate', duration: Infinity });

      const response = await api.post(
        `/api/validation/validation/${validationStatus.validation_id}/generate-pdf`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('PDF report generated successfully!', { id: 'pdf-generate' });

      await loadValidationStatus();
    } catch (error: any) {
      console.error('PDF generation error:', error);
      toast.error(error.response?.data?.error || 'Failed to generate PDF', { id: 'pdf-generate' });
    } finally {
      setGeneratingPDF(false);
    }
  };

  const downloadPDFReport = async () => {
    if (!validationStatus?.validation_id) return;

    try {
      const token = localStorage.getItem('jarvis_access_token');

      const response = await api.get(
        `/api/validation/validation/${validationStatus.validation_id}/download-pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `validation_report_${processMapId}_${Date.now()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('PDF report downloaded successfully!');
    } catch (error: any) {
      console.error('PDF download error:', error);
      toast.error('Failed to download PDF report');
    }
  };

  const sendGapsToInterviewQuestions = async () => {
    if (!validationStatus?.validation_id) return;

    const gapsCount = validationStatus.summary?.gap_detection?.total_missing_steps || 0;
    if (gapsCount === 0) {
      toast.error('No gaps found to convert to interview questions');
      return;
    }

    try {
      setSendingGaps(true);
      toast.loading(`Converting ${gapsCount} gaps to interview questions...`, { id: 'send-gaps' });

      const response = await api.post(
        `/api/validation/validation/${validationStatus.validation_id}/create-interview-questions`
      );

      toast.success(
        `Successfully created ${response.data.questions_created} interview questions from validation gaps!`,
        { id: 'send-gaps', duration: 5000 }
      );

    } catch (error: any) {
      console.error('Failed to send gaps to interview questions:', error);
      toast.error(
        error.response?.data?.error || 'Failed to create interview questions from gaps',
        { id: 'send-gaps' }
      );
    } finally {
      setSendingGaps(false);
    }
  };

  const downloadExcelReport = async () => {
    if (!validationStatus?.validation_id) return;

    try {
      const token = localStorage.getItem('jarvis_access_token');

      toast.loading('Generating Excel report...', { id: 'excel-download', duration: Infinity });

      const response = await api.get(
        `/api/validation/validation/${validationStatus.validation_id}/download-excel`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `validation_report_${processMapId}_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Excel report downloaded successfully!', { id: 'excel-download', duration: 4000 });
    } catch (error: any) {
      console.error('Excel download error:', error);
      toast.error('Failed to download Excel report', { id: 'excel-download' });
    }
  };

  const toggleStepExpansion = (index: number) => {
    setExpandedSteps(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const toggleGapExpansion = (index: number) => {
    setExpandedGaps(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'CRITICAL': return 'text-red-800 bg-red-100 border-red-300';
      case 'HIGH': return 'text-orange-700 bg-orange-100 border-orange-300';
      case 'MEDIUM': return 'text-yellow-700 bg-yellow-100 border-yellow-300';
      case 'LOW': return 'text-blue-700 bg-blue-100 border-blue-300';
      default: return 'text-gray-700 bg-gray-100 border-gray-300';
    }
  };

  const getAssessmentBadgeColor = (assessment?: string) => {
    switch (assessment) {
      case 'HIGH_CONFIDENCE': return 'bg-green-100 text-green-800 border-green-300';
      case 'MODERATE_CONFIDENCE': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'LOW_CONFIDENCE': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'REQUIRES_MANUAL_REVIEW': return 'bg-red-100 text-red-800 border-red-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getAssessmentText = (assessment?: string) => {
    switch (assessment) {
      case 'HIGH_CONFIDENCE': return 'High Confidence';
      case 'MODERATE_CONFIDENCE': return 'Moderate Confidence';
      case 'LOW_CONFIDENCE': return 'Low Confidence';
      case 'REQUIRES_MANUAL_REVIEW': return 'Requires Manual Review';
      default: return 'Unknown';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'CLEAR_MATCH': return 'text-green-600 bg-green-50';
      case 'INFERENCE': return 'text-blue-600 bg-blue-50';
      case 'AMBIGUITY': return 'text-yellow-600 bg-yellow-50';
      case 'MISSING': return 'text-red-600 bg-red-50';
      case 'NEEDS_REVIEW': return 'text-orange-600 bg-orange-50';
      case 'ERROR': return 'text-gray-600 bg-gray-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'CLEAR_MATCH': return '✅';
      case 'INFERENCE': return '🔍';
      case 'AMBIGUITY': return '⚠️';
      case 'MISSING': return '❌';
      case 'NEEDS_REVIEW': return '👁️';
      case 'ERROR': return '⚡';
      default: return '❓';
    }
  };

  const filteredResults = detailedReport?.validation_results?.filter(result => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'issues') {
      return result.validation_status !== 'CLEAR_MATCH' && result.validation_status !== 'ERROR';
    }
    return result.validation_status === filterStatus;
  }) || [];

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
        <div className="flex items-center justify-center py-8">
          <Loader className="w-6 h-6 animate-spin text-electric-blue" />
          <span className="ml-2 text-gray-600">Loading validation status...</span>
        </div>
      </div>
    );
  }

  if (!validationStatus?.has_validation) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200 mt-8">
        <div className="flex items-start space-x-3">
          <FileText className="w-5 h-5 text-electric-blue mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Source Documentation Validation
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Verify that this AI-generated process map is accurate and true to the source documentation.
              This generates a comprehensive report showing exact matches, inferences, ambiguities, and missing items.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
              <p className="text-xs text-blue-800">
                <strong>What this validates:</strong> Each process step is checked against your source documents
                to verify accuracy, identify assumptions, and flag any discrepancies or missing information.
              </p>
            </div>
            <button
              onClick={startValidation}
              disabled={validating}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-electric-blue hover:bg-darker-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-electric-blue disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {validating ? (
                <>
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  Validating...
                </>
              ) : (
                <>
                  <PlayCircle className="w-4 h-4 mr-2" />
                  Start Validation
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const summary = validationStatus.summary || {
    clear_matches: 0,
    ambiguities: 0,
    inferences: 0,
    missing: 0,
    needs_review: 0
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start space-x-3">
          {validationStatus.status === 'COMPLETED' && (
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
          )}
          {validationStatus.status === 'IN_PROGRESS' && (
            <Loader className="w-5 h-5 text-blue-600 animate-spin mt-0.5" />
          )}
          {validationStatus.status === 'FAILED' && (
            <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
          )}
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Validation Report
            </h3>
            <p className="text-sm text-gray-600">
              {validationStatus.status === 'COMPLETED' && `Completed ${new Date(validationStatus.completed_at!).toLocaleString()}`}
              {validationStatus.status === 'IN_PROGRESS' && 'In Progress...'}
              {validationStatus.status === 'FAILED' && 'Failed'}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getAssessmentBadgeColor(validationStatus.overall_assessment)}`}>
            {getAssessmentText(validationStatus.overall_assessment)}
          </span>
          <button
            onClick={loadValidationStatus}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            title="Refresh status"
          >
            <RefreshCw className="w-4 h-4 text-gray-600" />
          </button>
        </div>
      </div>

      {validationStatus.status === 'IN_PROGRESS' && (
        <div className="mb-4">
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <Loader className="w-4 h-4 text-blue-600 animate-spin" />
                <span className="text-sm font-medium text-blue-800">
                  Validation in progress...
                </span>
              </div>
              <span className="text-sm text-blue-600 font-semibold">
                {validationStatus.completed_steps !== undefined
                  ? `${validationStatus.completed_steps}/${validationStatus.total_steps} steps`
                  : 'Starting...'}
              </span>
            </div>

            {/* Batch Progress Indicator */}
            {validationStatus.current_batch !== undefined && validationStatus.total_batches !== undefined && (
              <div className="mb-2 flex items-center justify-between text-xs">
                <span className="text-blue-700 font-medium">
                  📦 Batch {validationStatus.current_batch}/{validationStatus.total_batches}
                  {validationStatus.parallelism && (
                    <span className="ml-2 text-blue-600">
                      ({validationStatus.parallelism} steps in parallel)
                    </span>
                  )}
                </span>
                {validationStatus.batch_size && (
                  <span className="text-blue-600">
                    {validationStatus.batch_size} steps/batch
                  </span>
                )}
              </div>
            )}

            {/* Progress Bar - Use actual progress percentage from batch processing */}
            {(validationStatus.progress_percentage !== undefined || validationStatus.estimated_progress !== undefined) && (
              <div className="w-full bg-blue-200 rounded-full h-2.5 mb-2">
                <div
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${validationStatus.progress_percentage || validationStatus.estimated_progress || 0}%` }}
                />
              </div>
            )}

            {/* Status Text */}
            <div className="flex items-center justify-between">
              <p className="text-xs text-blue-700">
                {validationStatus.completed_steps && validationStatus.completed_steps > 0 ? (
                  <>
                    Validated {validationStatus.completed_steps} of {validationStatus.total_steps || 0} steps.
                    {validationStatus.validation_results && validationStatus.validation_results.length > 0 && (
                      <span className="ml-1 font-medium text-blue-800">
                        Results loading incrementally ✨
                      </span>
                    )}
                  </>
                ) : (
                  <>Processing {validationStatus.total_steps || 0} process steps. This may take several minutes.</>
                )}
              </p>
              {validationStatus.progress_percentage !== undefined && (
                <span className="text-xs font-semibold text-blue-800">
                  {validationStatus.progress_percentage}%
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {validationStatus.status === 'FAILED' && (
        <div className="mb-4">
          <div className={`border rounded-md p-4 ${
            validationStatus.is_stuck
              ? 'bg-orange-50 border-orange-200'
              : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-start space-x-2">
              <AlertCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                validationStatus.is_stuck ? 'text-orange-600' : 'text-red-600'
              }`} />
              <div className="flex-1">
                <p className={`text-sm font-medium ${
                  validationStatus.is_stuck ? 'text-orange-800' : 'text-red-800'
                }`}>
                  {validationStatus.is_stuck ? 'Validation Timed Out' : 'Validation Failed'}
                </p>
                <p className={`text-sm mt-1 ${
                  validationStatus.is_stuck ? 'text-orange-700' : 'text-red-700'
                }`}>
                  {validationStatus.error_message || validationStatus.message || 'An error occurred during validation'}
                </p>
                {validationStatus.is_stuck && (
                  <div className="mt-2 p-2 bg-orange-100 border border-orange-300 rounded text-xs text-orange-800">
                    <strong>Note:</strong> The previous validation was stuck in processing for more than 30 minutes and has been automatically cancelled.
                    This may have occurred due to API issues or system errors. Please start a fresh validation.
                  </div>
                )}
                <button
                  onClick={startValidation}
                  disabled={validating}
                  className={`mt-3 inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-white ${
                    validationStatus.is_stuck
                      ? 'bg-orange-600 hover:bg-orange-700'
                      : 'bg-red-600 hover:bg-red-700'
                  } disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
                >
                  {validating ? (
                    <>
                      <Loader className="w-4 h-4 mr-1.5 animate-spin" />
                      Starting new validation...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-1.5" />
                      Start New Validation
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {validationStatus.status === 'COMPLETED' && (
        <>
          <div className="grid gap-4 mb-4">
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <div className="text-3xl font-bold text-purple-600">
                {validationStatus.match_percentage}%
              </div>
              <div className="text-sm text-gray-600 mt-1">Match Rate</div>
              <div className="text-xs text-gray-500 mt-1">
                {summary.clear_matches} of {validationStatus.total_steps} steps
              </div>
            </div>
           
          </div>

          <div className="space-y-2 mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-700 font-medium">Clear Matches</span>
              <span className="font-semibold text-green-600 flex items-center">
                ✅ {summary.clear_matches}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-700 font-medium">Inferences Made</span>
              <span className="font-semibold text-blue-600 flex items-center">
                🔍 {summary.inferences}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-700 font-medium">Ambiguities Found</span>
              <span className="font-semibold text-yellow-600 flex items-center">
                ⚠️ {summary.ambiguities}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <div className="flex flex-col">
                <span className="text-gray-700 font-medium">Missing from Source</span>
                <span className="text-xs text-gray-500 italic">Process map steps that couldn't be found in source documents</span>
              </div>
              <span className="font-semibold text-orange-600 flex items-center">
                ❌ {summary.missing}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-700 font-medium">Needs Review</span>
              <span className="font-semibold text-red-600 flex items-center">
                👁️ {summary.needs_review}
              </span>
            </div>
            {summary.errors && summary.errors > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-700 font-medium">Errors</span>
                <span className="font-semibold text-gray-600 flex items-center">
                  ⚡ {summary.errors}
                </span>
              </div>
            )}
          </div>

          {/* System Verification Summary - PROMINENT */}
          {detailedReport && (
            (() => {
              // Calculate system verification statistics
              const systemsNotVerified = detailedReport.validation_results.filter(result =>
                result.systems_involved_verification && !result.systems_involved_verification.explicitly_mentioned
              ).length;

              const systemsVerified = detailedReport.validation_results.filter(result =>
                result.systems_involved_verification && result.systems_involved_verification.explicitly_mentioned
              ).length;

              return systemsNotVerified > 0 && (
                <div className="mb-4 p-4 bg-cyan-50 rounded-lg border border-cyan-200">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-cyan-900 flex items-center">
                      🔧 System Verification
                    </h4>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded border border-orange-300">
                        ⚠️ {systemsNotVerified} not verified
                      </span>
                      {systemsVerified > 0 && (
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded border border-green-300">
                          ✅ {systemsVerified} verified
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-cyan-800 mb-2">
                    Some systems mentioned in process steps are not explicitly found in source documents
                  </p>
                  <div className="bg-cyan-100 p-2 rounded text-xs text-cyan-800">
                    💡 <strong>Tip:</strong> The terminology analysis helps identify systems from your documents.
                    Check the detailed results below for specific system verification details.
                  </div>
                </div>
              );
            })()
          )}

          {/* Gap Detection Summary - NEW */}
          {validationStatus.summary?.gap_detection && (
            <div className="mb-4 p-4 bg-red-50 rounded-lg border border-red-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex flex-col">
                  <h4 className="text-sm font-semibold text-red-900 flex items-center">
                    🔴 Gap Detection - Missing Steps
                  </h4>
                  <p className="text-xs text-red-700 mt-1">
                    Steps found in source documents but missing from your process map
                  </p>
                </div>
                <span className="text-xl font-bold text-red-600">
                  {validationStatus.summary.gap_detection.total_missing_steps}
                </span>
              </div>
              {validationStatus.summary.gap_detection.total_missing_steps > 0 ? (
                <>
                  <p className="text-xs text-red-800 mb-2">
                    The following steps exist in source documents but are NOT in your process map:
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {validationStatus.summary.gap_detection.critical_gaps > 0 && (
                      <div className="bg-red-100 p-2 rounded text-xs">
                        <span className="font-semibold text-red-900">Critical:</span>{' '}
                        <span className="text-red-700">{validationStatus.summary.gap_detection.critical_gaps}</span>
                      </div>
                    )}
                    {validationStatus.summary.gap_detection.high_gaps > 0 && (
                      <div className="bg-orange-100 p-2 rounded text-xs">
                        <span className="font-semibold text-orange-900">High:</span>{' '}
                        <span className="text-orange-700">{validationStatus.summary.gap_detection.high_gaps}</span>
                      </div>
                    )}
                    {validationStatus.summary.gap_detection.medium_gaps > 0 && (
                      <div className="bg-yellow-100 p-2 rounded text-xs">
                        <span className="font-semibold text-yellow-900">Medium:</span>{' '}
                        <span className="text-yellow-700">{validationStatus.summary.gap_detection.medium_gaps}</span>
                      </div>
                    )}
                    {validationStatus.summary.gap_detection.low_gaps > 0 && (
                      <div className="bg-blue-100 p-2 rounded text-xs">
                        <span className="font-semibold text-blue-900">Low:</span>{' '}
                        <span className="text-blue-700">{validationStatus.summary.gap_detection.low_gaps}</span>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={sendGapsToInterviewQuestions}
                    disabled={sendingGaps}
                    className="mt-3 w-full inline-flex items-center justify-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {sendingGaps ? (
                      <>
                        <Loader className="w-4 h-4 mr-2 animate-spin" />
                        Creating Interview Questions...
                      </>
                    ) : (
                      <>
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Send Gaps to Interview Questions (Step 6: Fill Gaps)
                      </>
                    )}
                  </button>
                </>
              ) : (
                <p className="text-xs text-green-700 bg-green-100 p-2 rounded border border-green-300">
                  ✅ No missing steps detected - process map covers all source document activities
                </p>
              )}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex flex-col gap-3">
            <button
              onClick={downloadExcelReport}
              className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-colors"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Excel
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ValidationReportPanel;
